import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { proxyAPI } from '../services/api';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

function ProxyTraffic() {
  const { id } = useParams();
  const [proxy, setProxy] = useState(null);
  const [trafficData, setTrafficData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);

  // Query parameters for traffic API
  const [duration, setDuration] = useState('1h');
  const [granularity, setGranularity] = useState('minute');

  useEffect(() => {
    fetchProxy();
  }, [id]);

  useEffect(() => {
    if (proxy) {
      fetchTraffic();
    }
  }, [proxy, duration, granularity]);

  useEffect(() => {
    let interval;
    if (autoRefresh && proxy) {
      interval = setInterval(() => {
        fetchTraffic(true);
      }, 30000); // Refresh every 30 seconds
    }
    return () => clearInterval(interval);
  }, [autoRefresh, proxy, duration, granularity]);

  const fetchProxy = async () => {
    try {
      const response = await proxyAPI.getById(id);
      setProxy(response.data);
    } catch (err) {
      setError('Failed to load proxy: ' + err.message);
      setLoading(false);
    }
  };

  const fetchTraffic = async (isRefresh = false) => {
    try {
      if (!isRefresh) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      const response = await proxyAPI.getTraffic(id, { duration, granularity });

      // Handle different response formats
      const data = response.data.data || response.data;
      setTrafficData(data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch traffic data: ' + err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const formatBandwidth = (bytes) => {
    if (!bytes) return '0 MB';
    const mb = bytes / (1024 * 1024);
    if (mb >= 1024) {
      return `${(mb / 1024).toFixed(2)} GB`;
    }
    return `${mb.toFixed(2)} MB`;
  };

  const prepareChartData = () => {
    if (!trafficData || !trafficData.products) return [];

    const allTimestamps = new Set();
    trafficData.products.forEach(product => {
      if (product.stats) {
        Object.keys(product.stats).forEach(ts => allTimestamps.add(ts));
      }
    });

    const sortedTimestamps = Array.from(allTimestamps).sort();

    return sortedTimestamps.map(timestamp => {
      const dataPoint = { timestamp };
      trafficData.products.forEach(product => {
        dataPoint[product.name] = product.stats[timestamp] || 0;
      });
      return dataPoint;
    });
  };

  if (loading && !trafficData) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error && !proxy) {
    return (
      <div className="card">
        <div className="alert alert-danger">{error}</div>
        <Link to="/proxies" className="btn btn-secondary">Back to Proxies</Link>
      </div>
    );
  }

  const chartData = trafficData ? prepareChartData() : [];
  const totalBandwidth = trafficData?.products?.reduce((sum, p) => sum + (p.totalBandwidth || 0), 0) || 0;

  return (
    <div>
      <div className="card">
        <div className="flex-between mb-3">
          <div>
            <h2>Traffic Stats: {proxy?.name}</h2>
            <p className="text-muted">{proxy?.description}</p>
          </div>
          <Link to={`/proxies/${id}`} className="btn btn-secondary">
            Back to Proxy
          </Link>
        </div>

        {!proxy?.trafficApi?.endpoint ? (
          <div className="alert alert-warning">
            This proxy does not have a traffic API configured.
            <Link to={`/proxies/${id}`}> Configure it now</Link>
          </div>
        ) : (
          <>
            {/* Controls */}
            <div className="grid grid-3 mb-3">
              <div className="form-group">
                <label>Duration</label>
                <select value={duration} onChange={(e) => setDuration(e.target.value)}>
                  <option value="1h">Last Hour</option>
                  <option value="24h">Last 24 Hours</option>
                  <option value="7d">Last 7 Days</option>
                  <option value="30d">Last 30 Days</option>
                </select>
              </div>

              <div className="form-group">
                <label>Granularity</label>
                <select value={granularity} onChange={(e) => setGranularity(e.target.value)}>
                  <option value="minute">Minute</option>
                  <option value="hour">Hour</option>
                  <option value="day">Day</option>
                </select>
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={autoRefresh}
                    onChange={(e) => setAutoRefresh(e.target.checked)}
                    style={{ width: 'auto' }}
                  />
                  Auto-refresh (30s)
                </label>
                <button
                  onClick={() => fetchTraffic(true)}
                  className="btn btn-sm btn-secondary mt-2"
                  disabled={refreshing}
                >
                  {refreshing ? 'Refreshing...' : 'Refresh Now'}
                </button>
              </div>
            </div>

            {error && (
              <div className="alert alert-danger">{error}</div>
            )}

            {trafficData && trafficData.products ? (
              <>
                {/* Summary Cards */}
                <div className="grid grid-3 mb-4">
                  <div className="card" style={{ backgroundColor: '#f8f9fa', padding: '1.5rem' }}>
                    <h4 style={{ margin: '0 0 0.5rem 0', color: '#6c757d' }}>Total Bandwidth</h4>
                    <h2 style={{ margin: 0, color: '#28a745' }}>{formatBandwidth(totalBandwidth)}</h2>
                  </div>

                  {trafficData.products.map(product => (
                    <div key={product.code} className="card" style={{ backgroundColor: '#f8f9fa', padding: '1.5rem' }}>
                      <h4 style={{ margin: '0 0 0.5rem 0', color: '#6c757d' }}>{product.name}</h4>
                      <h3 style={{ margin: 0 }}>{formatBandwidth(product.totalBandwidth)}</h3>
                    </div>
                  ))}
                </div>

                {/* Chart */}
                {chartData.length > 0 ? (
                  <div className="mb-4">
                    <h3>Bandwidth Usage Over Time</h3>
                    <ResponsiveContainer width="100%" height={400}>
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="timestamp"
                          angle={-45}
                          textAnchor="end"
                          height={100}
                          tick={{ fontSize: 12 }}
                        />
                        <YAxis
                          label={{ value: 'Bandwidth (MB)', angle: -90, position: 'insideLeft' }}
                        />
                        <Tooltip
                          formatter={(value) => formatBandwidth(value)}
                        />
                        <Legend />
                        {trafficData.products.map((product, index) => {
                          const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#8dd1e1'];
                          return (
                            <Line
                              key={product.code}
                              type="monotone"
                              dataKey={product.name}
                              stroke={colors[index % colors.length]}
                              strokeWidth={2}
                              dot={{ r: 3 }}
                            />
                          );
                        })}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="alert alert-info">
                    No traffic data available for the selected time range.
                  </div>
                )}

                {/* Detailed Stats Table */}
                <div className="mt-4">
                  <h3>Product Breakdown</h3>
                  <div className="table-responsive">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Code</th>
                          <th>Total Bandwidth</th>
                          <th>Data Points</th>
                        </tr>
                      </thead>
                      <tbody>
                        {trafficData.products.map(product => (
                          <tr key={product.code}>
                            <td>{product.name}</td>
                            <td><code>{product.code}</code></td>
                            <td>{formatBandwidth(product.totalBandwidth)}</td>
                            <td>
                              {product.stats ? Object.keys(product.stats).length : 0} time points
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            ) : (
              <div className="alert alert-info">
                Loading traffic data...
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ProxyTraffic;
